import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DayView, POI, Tag, TripLeg } from "@poi-planner/shared";
import type { TripSidebarController } from "../../hooks/useTripSidebarController";
import type { MapBounds } from "../../hooks/usePoiFilters";
import { useElementHeight } from "../../hooks/useElementHeight";
import { isOwnPoi } from "../../store/libraryStore";
import { useLegStore } from "../../store/legStore";
import { useTripStore } from "../../store/tripStore";
import { useUIStore, type PhoneBoardView } from "../../store/uiStore";
import { useConnectivityStore } from "../../store/connectivityStore";
import { formatShortDay } from "../../utils/format";
import type { PlannerMapContext } from "../../utils/plannerMapContext";
import { Button } from "../Button";
import { PlannerPane } from "../PlannerPane";
import { TripMapPane } from "../TripMapPane";
import { buildActiveFilters, summarizeFilters } from "./activeFilters";
import { PhoneBoardToolbar } from "./PhoneBoardToolbar";
import { PoiFilterDialog } from "./PoiFilterDialog";
import {
  RESULTS_COLLAPSED_HEIGHT,
  RESULTS_FULL_FALLBACK_HEIGHT,
  RESULTS_PEEK_HEIGHT,
  RESULTS_TALL_HEIGHT,
  ResultsCarousel,
} from "./ResultsCarousel";
import { SchedulePoiPicker, type ScheduledPick } from "./SchedulePoiPicker";

const TOAST_DURATION_MS = 10_000;
/** Space kept above the tall results card so the view behind shows. */
const TALL_CARD_TOP_GAP = 24;

interface BoardToast {
  id: number;
  message: string;
  undoable: boolean;
}

interface PhoneTripBoardProps {
  controller: TripSidebarController;
  allDayViews: DayView[];
  plannerDayViews: DayView[];
  activeLeg: TripLeg | null;
  plannerMapContext: PlannerMapContext;
  tags: Tag[];
  selectedPoiId: string | null;
  flyToTrigger: number;
  onSelectPoi: (poiId: string | null) => void;
  boundsFilterEnabled: boolean;
  onBoundsFilterChange: (enabled: boolean) => void;
  onBoundsChange: (bounds: MapBounds) => void;
  fitKey: string;
}

/**
 * Phone trip board: one full-screen view (Planner or Map) under a toolbar,
 * with the results card pinned to the bottom in place of the Sidebar drawer.
 * Filtering, selection and the card height live in stores, so rotating to
 * the tablet layout and back keeps them.
 */
export function PhoneTripBoard({
  controller,
  allDayViews,
  plannerDayViews,
  activeLeg,
  plannerMapContext,
  tags,
  selectedPoiId,
  flyToTrigger,
  onSelectPoi,
  boundsFilterEnabled,
  onBoundsFilterChange,
  onBoundsChange,
  fitKey,
}: PhoneTripBoardProps) {
  const offline = useConnectivityStore((s) => s.mode === "offline");
  const readOnly = useTripStore((s) => s.isReadOnly());
  const schedulePOI = useTripStore((s) => s.schedulePOI);
  const undo = useTripStore((s) => s.undo);
  const legs = useLegStore((s) => s.legs);
  const requestedView = useUIStore((s) => s.phoneBoardView);
  const setPhoneBoardView = useUIStore((s) => s.setPhoneBoardView);
  const requestedCardHeight = useUIStore((s) => s.resultsCardHeight);
  const setCardHeight = useUIStore((s) => s.setResultsCardHeight);
  const expandAll = useUIStore((s) => s.sidebarExpandAll);
  const setExpandAll = useUIStore((s) => s.setSidebarExpandAll);
  const plannerFocusDate = useUIStore((s) => s.plannerFocusDate);
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const setSelectedMapPoiId = useUIStore((s) => s.setSelectedMapPoiId);

  // Mapbox cannot work offline: the Planner stands in without changing the
  // requested view, which returns on reconnect.
  const view: PhoneBoardView = offline ? "planner" : requestedView;
  // Collapsing is a Planner affordance; the Map view keeps its results
  // visible and shows a collapsed card at peek height.
  const cardHeight =
    requestedCardHeight === "collapsed" && view !== "planner"
      ? "peek"
      : requestedCardHeight;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [schedulingPoi, setSchedulingPoi] = useState<POI | null>(null);
  const [toast, setToast] = useState<BoardToast | null>(null);

  const [boardRef, boardHeight] = useElementHeight<HTMLDivElement>();
  const [toolbarRef, toolbarHeight] = useElementHeight<HTMLDivElement>();
  const measured = boardHeight > 0;
  const tallHeight = measured
    ? Math.max(
        RESULTS_PEEK_HEIGHT,
        Math.min(
          RESULTS_TALL_HEIGHT,
          boardHeight - toolbarHeight - TALL_CARD_TOP_GAP,
        ),
      )
    : RESULTS_TALL_HEIGHT;
  // Full height reaches up to the toolbar, keeping search and Filters
  const fullHeight = measured
    ? Math.max(tallHeight, boardHeight - toolbarHeight)
    : RESULTS_FULL_FALLBACK_HEIGHT;
  const full = cardHeight === "full";
  const cardPixelHeight = {
    collapsed: RESULTS_COLLAPSED_HEIGHT,
    peek: RESULTS_PEEK_HEIGHT,
    tall: tallHeight,
    full: fullHeight,
  }[cardHeight];
  // What the card hides of the view beneath. Behind the full card the view
  // keeps its peek layout (so the Planner keeps its height and the map its
  // padding) and is made inert.
  const coveredHeight = full ? RESULTS_PEEK_HEIGHT : cardPixelHeight;

  const cardSnaps = useMemo(
    () => [
      ...(view === "planner"
        ? [{ key: "collapsed" as const, px: RESULTS_COLLAPSED_HEIGHT }]
        : []),
      { key: "peek" as const, px: RESULTS_PEEK_HEIGHT },
      { key: "tall" as const, px: tallHeight },
      { key: "full" as const, px: fullHeight },
    ],
    [view, tallHeight, fullHeight],
  );

  const { filteredPOIs } = controller;
  const activeFilters = buildActiveFilters(controller, boundsFilterEnabled);

  const resultNumberByPoiId = useMemo(
    () => new Map(filteredPOIs.map((poi, index) => [poi.id, index + 1])),
    [filteredPOIs],
  );

  // A filter change that removes the selected result clears the selection
  // (without panning). Selections that were never results — a Planner pin —
  // are left alone.
  const previousResultIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const previous = previousResultIdsRef.current;
    previousResultIdsRef.current = new Set(resultNumberByPoiId.keys());
    if (
      selectedPoiId &&
      previous.has(selectedPoiId) &&
      !resultNumberByPoiId.has(selectedPoiId)
    ) {
      setSelectedMapPoiId(null);
    }
  }, [resultNumberByPoiId, selectedPoiId, setSelectedMapPoiId]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const showToast = useCallback((message: string, undoable = false) => {
    setToast({ id: Date.now(), message, undoable });
  }, []);

  const resetFilters = useCallback(() => {
    controller.clearAllFilters();
    controller.setHideScheduled(false);
    onBoundsFilterChange(false);
  }, [controller, onBoundsFilterChange]);

  const showOnMap = useCallback(
    (poiId: string) => {
      onSelectPoi(poiId);
      setPhoneBoardView("map");
    },
    [onSelectPoi, setPhoneBoardView],
  );

  const handleConfirmSchedule = (pick: ScheduledPick) => {
    const poi = schedulingPoi;
    setSchedulingPoi(null);
    if (!poi) return;
    schedulePOI(pick.poiId, pick.startTime);
    showToast(
      `${poi.name} added to ${formatShortDay(pick.date)} at ${pick.timeLabel}`,
      true,
    );
  };

  const canEdit = !offline && !readOnly;
  const canEditPoi = useCallback(
    (poi: POI) => !offline && isOwnPoi(poi),
    [offline],
  );
  const openPoi = useCallback(
    (poi: POI) => openPOIEditor("edit", poi.id),
    [openPOIEditor],
  );
  const togglePin = useCallback(
    (poiId: string) => {
      void controller.onTogglePin(poiId).then((message) => {
        if (message) showToast(message);
      });
    },
    [controller, showToast],
  );

  const toolbar = (
    <PhoneBoardToolbar
      view={view}
      floating={view === "map" && !full}
      onViewChange={offline ? undefined : setPhoneBoardView}
      search={controller.search}
      onSearchChange={controller.setSearch}
      activeFilterCount={activeFilters.length}
      onOpenFilters={() => setFiltersOpen(true)}
    />
  );

  return (
    <div
      ref={boardRef}
      className="relative flex min-h-0 min-w-0 flex-1 flex-col"
      data-testid="phone-board"
    >
      {view === "planner" ? (
        <>
          <div ref={toolbarRef}>{toolbar}</div>
          <div
            className="flex min-h-0 flex-1 flex-col"
            inert={full}
            // Runtime geometry: the last hours scroll clear of the card
            style={{ paddingBottom: coveredHeight }}
          >
            <PlannerPane
              allDayViews={allDayViews}
              visibleDayViews={plannerDayViews}
              activeLeg={activeLeg}
              onShowOnMap={offline ? undefined : showOnMap}
              compact
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col" inert={full}>
            <TripMapPane
              plannerContext={plannerMapContext}
              sidebarPois={filteredPOIs}
              tags={tags}
              selectedPoiId={selectedPoiId}
              flyToTrigger={flyToTrigger}
              onSelectPoi={onSelectPoi}
              onBoundsChange={onBoundsChange}
              boundsFilterEnabled={boundsFilterEnabled}
              fitKey={fitKey}
              overlayInsets={{ top: toolbarHeight, bottom: coveredHeight }}
              resultNumberByPoiId={resultNumberByPoiId}
            />
          </div>
          <div ref={toolbarRef} className="absolute inset-x-0 top-0 z-30">
            {toolbar}
          </div>
        </>
      )}

      <ResultsCarousel
        pois={filteredPOIs}
        totalPoiCount={controller.totalPoiCount}
        subtitle={summarizeFilters(activeFilters, controller.sortMode)}
        height={cardHeight}
        collapsible={view === "planner"}
        pixelHeight={cardPixelHeight}
        snaps={cardSnaps}
        onHeightChange={setCardHeight}
        expandAll={expandAll}
        onExpandAllChange={setExpandAll}
        selectedPoiId={selectedPoiId}
        onSelectPoi={onSelectPoi}
        scheduledCounts={controller.scheduledCounts}
        distanceMap={controller.distanceMap}
        targetPoiId={controller.targetPoiId}
        pinnedPoiIds={controller.pinnedPoiIds}
        onCreate={canEdit ? () => openPOIEditor("create") : undefined}
        onClearFilters={resetFilters}
        onShowOnMap={view === "planner" && !offline ? showOnMap : undefined}
        onSchedule={canEdit ? setSchedulingPoi : undefined}
        onSetTarget={controller.setTargetPoiId}
        onTogglePin={canEdit ? togglePin : undefined}
        onOpen={openPoi}
        canEditPoi={canEditPoi}
      />

      {toast && (
        <div
          key={toast.id}
          role="status"
          className="absolute inset-x-3 z-40 flex items-center gap-3 rounded-xl bg-primary py-1 pr-1 pl-4 text-sm text-white shadow-lg"
          // Runtime geometry: sits just above the results card, or over the
          // bottom of the full-height list
          style={{ bottom: (full ? 0 : cardPixelHeight) + 12 }}
        >
          <span className="min-w-0 flex-1 py-2">{toast.message}</span>
          {toast.undoable && (
            <Button
              variant="unstyled"
              aria-label="Undo scheduling"
              className="min-h-11 cursor-pointer rounded-lg px-3 font-bold text-white hover:bg-white/10"
              onClick={() => {
                undo();
                setToast(null);
              }}
            >
              Undo
            </Button>
          )}
        </div>
      )}

      <PoiFilterDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        controller={controller}
        boundsFilterEnabled={boundsFilterEnabled}
        boundsFilterAvailable={view === "map"}
        onBoundsFilterChange={onBoundsFilterChange}
        onReset={resetFilters}
      />

      {schedulingPoi && (
        <SchedulePoiPicker
          key={schedulingPoi.id}
          poi={schedulingPoi}
          dayViews={allDayViews}
          legs={legs}
          defaultDate={plannerFocusDate}
          onClose={() => setSchedulingPoi(null)}
          onConfirm={handleConfirmSchedule}
        />
      )}
    </div>
  );
}
